/**
 * Ticket Z14: Pflegeformular „Seite 100 Tage".
 *
 * Texte pflegen Administration und Redaktion. Das Weiterleitungsziel darf nur
 * die Administration setzen; es ist die einzige Bedienoberfläche dafür und
 * schreibt direkt in die Weiterleitungsliste (einzige Quelle).
 */

import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  getLandingAdmin,
  saveLandingContent,
  setLandingRedirect,
} from "@/lib/landing-hundert-tage.functions";
import {
  LANDING_PATH,
  LANDING_TRAINING_PATH,
  PRINT_ADDRESS_WARNING,
  REDIRECT_SINGLE_SOURCE_HINT,
  type AdminLandingContent,
} from "@/lib/landing-hundert-tage";
import { useAdminSession } from "@/hooks/use-admin-session";

export const Route = createFileRoute("/_admin/admin/landing-hundert-tage")({
  component: LandingHundertTagePage,
});

const inputClass =
  "w-full rounded-sm border border-foreground/20 bg-background px-3 py-2 font-body text-sm text-foreground focus:border-primary focus:outline-none";
const labelClass = "block font-display text-sm font-extrabold text-foreground";

function LandingHundertTagePage() {
  const loadFn = useServerFn(getLandingAdmin);
  const saveFn = useServerFn(saveLandingContent);
  const redirectFn = useServerFn(setLandingRedirect);
  const queryClient = useQueryClient();
  const { isAdmin } = useAdminSession();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-landing-hundert-tage"],
    queryFn: () => loadFn(),
  });

  const [form, setForm] = useState<AdminLandingContent | null>(null);
  const [redirectTarget, setRedirectTarget] = useState("");

  useEffect(() => {
    if (data) {
      setForm(data);
      setRedirectTarget(data.redirectTarget ?? "");
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async (values: AdminLandingContent) =>
      saveFn({
        data: {
          title: values.title,
          introMd: values.introMd,
          cockpitNoteMd: values.cockpitNoteMd,
          trainingNoteMd: values.trainingNoteMd,
          contactEmail: values.contactEmail,
          contactPhone: values.contactPhone,
          ctaLabel: values.ctaLabel,
          seoTitle: values.seoTitle,
          seoDescription: values.seoDescription,
          internalNote: values.internalNote,
        },
      }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(["admin-landing-hundert-tage"], fresh);
      toast.success("Inhalte gespeichert.");
    },
    onError: (e: Error) => toast.error(e.message || "Speichern fehlgeschlagen."),
  });

  const applyRedirect = useMutation({
    mutationFn: async (target: string) => redirectFn({ data: { target: target || null } }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(["admin-landing-hundert-tage"], fresh);
      setRedirectTarget(fresh.redirectTarget ?? "");
      toast.success(
        fresh.redirectTarget
          ? "Weiterleitung gesetzt. In Einzelfällen dauert es bis zu 60 Sekunden."
          : "Weiterleitung entfernt. Die Seite wird wieder angezeigt.",
      );
    },
    onError: (e: Error) => toast.error(e.message || "Weiterleitung fehlgeschlagen."),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 font-body text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Wird geladen …
      </div>
    );
  }
  if (isError || !form) {
    return (
      <p className="p-8 font-body text-sm text-destructive">
        {(error as Error)?.message ?? "Inhalte konnten nicht geladen werden."}
      </p>
    );
  }

  const set = <K extends keyof AdminLandingContent>(key: K, value: AdminLandingContent[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (form) save.mutate(form);
  };

  return (
    <div className="space-y-10 p-6 md:p-8">
      <header className="space-y-4">
        <h1 className="font-display text-2xl font-extrabold text-primary">Seite 100 Tage</h1>
        <div className="flex gap-3 rounded-sm border-l-4 border-brand-accent bg-brand-accent/10 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-brand-accent" aria-hidden />
          <div className="space-y-1 font-body text-sm text-foreground">
            <p className="font-display font-extrabold">Bitte nicht löschen oder umbenennen</p>
            <p>{PRINT_ADDRESS_WARNING}</p>
            <p className="text-muted-foreground">
              Betroffene Adressen: {LANDING_PATH} und {LANDING_TRAINING_PATH}
            </p>
          </div>
        </div>
        <a
          href={LANDING_PATH}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 font-body text-sm text-primary underline-offset-4 hover:underline"
        >
          Seite ansehen <ExternalLink className="h-4 w-4" aria-hidden />
        </a>
      </header>

      <form onSubmit={onSubmit} className="space-y-6">
        <div>
          <label className={labelClass} htmlFor="lp-title">
            Überschrift
          </label>
          <input
            id="lp-title"
            className={`${inputClass} mt-2`}
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="lp-intro">
            Einordnung (Markdown)
          </label>
          <textarea
            id="lp-intro"
            rows={8}
            className={`${inputClass} mt-2 resize-y`}
            value={form.introMd}
            onChange={(e) => set("introMd", e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="lp-cockpit">
            Hinweis auf das Kampagnencockpit (Markdown, leer = Abschnitt ausblenden)
          </label>
          <textarea
            id="lp-cockpit"
            rows={5}
            className={`${inputClass} mt-2 resize-y`}
            value={form.cockpitNoteMd}
            onChange={(e) => set("cockpitNoteMd", e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="lp-training">
            Einleitung der Weiterbildungsseite (Markdown)
          </label>
          <textarea
            id="lp-training"
            rows={5}
            className={`${inputClass} mt-2 resize-y`}
            value={form.trainingNoteMd}
            onChange={(e) => set("trainingNoteMd", e.target.value)}
          />
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <label className={labelClass} htmlFor="lp-email">
              Kontakt-E-Mail
            </label>
            <input
              id="lp-email"
              type="email"
              className={`${inputClass} mt-2`}
              value={form.contactEmail}
              onChange={(e) => set("contactEmail", e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="lp-phone">
              Telefonnummer
            </label>
            <input
              id="lp-phone"
              className={`${inputClass} mt-2`}
              value={form.contactPhone}
              onChange={(e) => set("contactPhone", e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="lp-cta">
              Text der Schaltfläche
            </label>
            <input
              id="lp-cta"
              className={`${inputClass} mt-2`}
              value={form.ctaLabel}
              onChange={(e) => set("ctaLabel", e.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="lp-seo-title">
              Titel für Suchmaschinen
            </label>
            <input
              id="lp-seo-title"
              className={`${inputClass} mt-2`}
              value={form.seoTitle}
              onChange={(e) => set("seoTitle", e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="lp-seo-desc">
              Beschreibung für Suchmaschinen
            </label>
            <textarea
              id="lp-seo-desc"
              rows={3}
              className={`${inputClass} mt-2 resize-y`}
              value={form.seoDescription}
              onChange={(e) => set("seoDescription", e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className={labelClass} htmlFor="lp-note">
            Interne Notiz (erscheint nie auf der Website)
          </label>
          <textarea
            id="lp-note"
            rows={3}
            className={`${inputClass} mt-2 resize-y`}
            value={form.internalNote}
            onChange={(e) => set("internalNote", e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={save.isPending}
          className="inline-flex items-center gap-2 rounded-sm bg-brand-button px-5 py-2.5 font-display text-sm font-extrabold text-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Inhalte speichern
        </button>
      </form>

      <section className="space-y-4 rounded-sm border border-foreground/15 p-6">
        <h2 className="font-display text-lg font-extrabold text-primary">Weiterleitungsziel</h2>
        <p className="max-w-[70ch] font-body text-sm text-muted-foreground">
          Leer lassen, solange die Seite angezeigt werden soll. Wird hier ein Ziel eingetragen,
          leitet {LANDING_PATH} dauerhaft (301) dorthin weiter. {REDIRECT_SINGLE_SOURCE_HINT}
        </p>
        {!isAdmin && (
          <p className="font-body text-sm text-muted-foreground">
            Nur die Administration darf das Weiterleitungsziel ändern.
          </p>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            aria-label="Weiterleitungsziel"
            placeholder="/neues-ziel oder https://…"
            className={inputClass}
            value={redirectTarget}
            disabled={!isAdmin || applyRedirect.isPending}
            onChange={(e) => setRedirectTarget(e.target.value)}
          />
          <button
            type="button"
            disabled={!isAdmin || applyRedirect.isPending}
            onClick={() => applyRedirect.mutate(redirectTarget.trim())}
            className="inline-flex shrink-0 items-center gap-2 rounded-sm border border-primary px-5 py-2.5 font-display text-sm font-extrabold text-primary transition-colors hover:bg-primary/5 disabled:opacity-50"
          >
            {applyRedirect.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {redirectTarget.trim() ? "Weiterleitung setzen" : "Weiterleitung entfernen"}
          </button>
        </div>
        <p className="font-body text-sm text-foreground">
          Aktueller Stand:{" "}
          {form.redirectTarget ? (
            <span className="font-display font-extrabold">{form.redirectTarget}</span>
          ) : (
            <span className="text-muted-foreground">keine Weiterleitung, Seite wird angezeigt</span>
          )}
        </p>
      </section>
    </div>
  );
}
